/**
 * Glue (rules v2) ribbon: a strip of sticky tape across the seam of a glued pair. It sits on
 * the pair's layer just above its two tiles (tiles of higher layers still cover it), dims with
 * them while either is covered, and snaps off with a small pop when the pair leaves the board.
 * Code-drawn like the board tray: the texture is baked once per game into a canvas.
 */
import * as Phaser from 'phaser';
import { C, css } from '../game/theme.js';
import { deco, dur } from '../fx/motion.js';

const KEY = 'fx_glue_ribbon';
const TW = 176;
const TH = 96;
const COVERED_TINT = 0x999999; // same 40% darker as covered tiles (GP-01.1)

/** Tape body with torn (zigzag) ends, diagonal stripes, ink outline and a soft drop shadow. */
function bake(scene: Phaser.Scene): void {
  if (scene.textures.exists(KEY)) return;
  const c = document.createElement('canvas');
  c.width = TW;
  c.height = TH;
  const ctx = c.getContext('2d')!;
  const x0 = 14;
  const x1 = TW - 14;
  const y0 = 16;
  const y1 = TH - 22;
  const teeth = 4;
  const tooth = 9;
  const path = (dx: number, dy: number) => {
    ctx.beginPath();
    ctx.moveTo(x0 + dx, y0 + dy);
    ctx.lineTo(x1 + dx, y0 + dy);
    for (let k = 1; k <= teeth * 2; k++) ctx.lineTo(x1 + dx + (k % 2 ? tooth : 0), y0 + dy + ((y1 - y0) * k) / (teeth * 2));
    ctx.lineTo(x0 + dx, y1 + dy);
    for (let k = teeth * 2 - 1; k >= 0; k--) ctx.lineTo(x0 + dx - (k % 2 ? tooth : 0), y0 + dy + ((y1 - y0) * k) / (teeth * 2));
    ctx.closePath();
  };
  ctx.lineJoin = 'round';
  // shadow
  path(0, 7);
  ctx.fillStyle = 'rgba(74,44,42,0.28)';
  ctx.fill();
  // body
  path(0, 0);
  ctx.fillStyle = css(C.mustard);
  ctx.fill();
  ctx.save();
  ctx.clip();
  ctx.strokeStyle = 'rgba(255,255,255,0.55)';
  ctx.lineWidth = 9;
  for (let x = -TH; x < TW + TH; x += 26) {
    ctx.beginPath();
    ctx.moveTo(x, y1 + 4);
    ctx.lineTo(x + (y1 - y0) + 8, y0 - 4);
    ctx.stroke();
  }
  ctx.fillStyle = 'rgba(255,255,255,0.5)';
  ctx.fillRect(x0, y0 + 6, x1 - x0, 6);
  ctx.fillStyle = css(C.mustardDark);
  ctx.globalAlpha = 0.45;
  ctx.fillRect(x0, y1 - 8, x1 - x0, 8);
  ctx.restore();
  ctx.strokeStyle = css(C.ink);
  ctx.lineWidth = 5;
  path(0, 0);
  ctx.stroke();
  scene.textures.addCanvas(KEY, c);
}

export class GlueRibbon extends Phaser.GameObjects.Image {
  covered = false;
  private shade = 1;
  private readonly baseScale: { x: number; y: number };

  /** `a`, `b`: the pair's tile centres; `tile`: the tile size in device px. */
  constructor(scene: Phaser.Scene, a: { x: number; y: number }, b: { x: number; y: number }, tile: number, tilt: number) {
    bake(scene);
    super(scene, (a.x + b.x) / 2, (a.y + b.y) / 2, KEY);
    const vertical = Math.abs(b.y - a.y) > Math.abs(b.x - a.x);
    // across the seam: ~0.3 tile into each tile, a third of a tile wide
    this.setDisplaySize(tile * 0.6, tile * 0.34);
    this.baseScale = { x: this.scaleX, y: this.scaleY };
    this.setAngle((vertical ? 90 : 0) + tilt);
    scene.add.existing(this);
  }

  /** Stick on (first build, or the pair came back with an undo): pop in after `delayMs`. */
  appear(delayMs: number): void {
    const d = deco(180);
    if (!d) {
      this.setScale(this.baseScale.x, this.baseScale.y);
      if (!delayMs) return;
      this.setVisible(false);
      this.scene.time.delayedCall(delayMs, () => this.active && this.setVisible(true));
      return;
    }
    this.setScale(0.01, this.baseScale.y);
    this.scene.tweens.add({ targets: this, scaleX: this.baseScale.x, delay: delayMs, duration: d, ease: 'Back.easeOut' });
  }

  setCovered(covered: boolean, animate: boolean): void {
    if (covered === this.covered) return;
    this.covered = covered;
    const to = covered ? 0 : 1;
    const d = animate ? dur(180) : 0;
    if (!d) {
      this.shade = to;
      this.applyShade();
      return;
    }
    this.scene.tweens.addCounter({
      from: this.shade,
      to,
      duration: d,
      onUpdate: (tw) => {
        this.shade = tw.getValue() ?? to;
        this.applyShade();
      },
    });
  }

  private applyShade(): void {
    if (this.shade >= 1) return void this.clearTint();
    if (this.shade <= 0) return void this.setTint(COVERED_TINT);
    const v = Math.round(0x99 + (0xff - 0x99) * this.shade);
    this.setTint((v << 16) | (v << 8) | v);
  }

  /** The pair left the board: the tape stretches, tears and fades. `onPop` fires at the tear. */
  snap(onPop: ((x: number, y: number) => void) | null): void {
    this.scene.tweens.killTweensOf(this);
    const d = dur(160);
    onPop?.(this.x, this.y);
    if (!d) return void this.destroy();
    this.setDepth(6500);
    this.scene.tweens.add({
      targets: this,
      scaleX: this.baseScale.x * 1.35,
      scaleY: this.baseScale.y * 0.6,
      alpha: 0,
      angle: this.angle + (deco(1) ? 14 : 0),
      duration: d,
      ease: 'Cubic.easeOut',
      onComplete: () => this.destroy(),
    });
  }

  /** Win sweep: fade with the leftover tiles. */
  fadeOut(): void {
    this.scene.tweens.killTweensOf(this);
    const d = dur(260);
    if (!d) return void this.setVisible(false);
    this.scene.tweens.add({ targets: this, alpha: 0, duration: d, onComplete: () => this.setVisible(false) });
  }
}
